// src/components/tutor/session_logs/SatPlanProgressPanel.js
//
// Materials-step "SAT Plan Progress" — rebuilt to match the clean reference
// demo: a tidy row list per subject, a thick draggable slider for completion %,
// a circular ± control for hours worked this session, an auto-saving "Saved /
// Saving…" pill, and a chevron that expands progress history. No clutter, no
// per-item Update buttons. Saves are idempotent per session.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import {
  fetchPlansForStudentDir,
  upsertItemSessionProgress,
  itemProgressPct,
} from "../../../utils/sat/satPlanData";
import { subjectFromSessionType, planMatchesSessionSubject, preferActivePlans } from "../../../utils/plan/planMatch";
import PlanProgressHistory from "../../sat/PlanProgressHistory";
import { planSubjectOrder, planTypeOf, PLAN_TYPES } from "../../../utils/plan/planConfig";

const ACCENT = { done: "#059669", in_progress: "#f59e0b", todo: "#cbd5e1" };
const statusOf = (pct) => (pct >= 100 ? "done" : pct > 0 ? "in_progress" : "todo");

export default function SatPlanProgressPanel({ studentDirId, sessionSubject, ...rest }) {
  const [allPlans, setAllPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const plans = await fetchPlansForStudentDir(studentDirId);
        if (alive) setAllPlans(plans || []);
      } catch {
        if (alive) setAllPlans([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [studentDirId]);

  // Every plan that covers the session's subject, active ones first. A student
  // can have several active plans as long as their subjects differ.
  const matching = useMemo(
    () =>
      preferActivePlans(
        (allPlans || [])
          .filter((p) => p.status !== "archived")
          .filter((p) => (p.checklist || []).some((i) => !i.isStretch))
          .filter((p) => planMatchesSessionSubject(p, sessionSubject))
      ).sort((a, b) => (a.status === "active" ? -1 : 0) - (b.status === "active" ? -1 : 0)),
    [allPlans, sessionSubject]
  );

  if (loading || !matching.length) return null;
  return (
    <>
      {matching.map((plan) => (
        <PlanProgressSection key={plan.id} plan={plan} sessionSubject={sessionSubject} {...rest} />
      ))}
    </>
  );
}

function PlanProgressSection({ plan: initialPlan, tutorUid, tutorName, sessionLogId, sessionSubject }) {
  const [plan, setPlan] = useState(initialPlan);
  const [edits, setEdits] = useState({});
  const [expanded, setExpanded] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const timers = useRef({});
  const pending = useRef(0);

  useEffect(() => {
    setPlan(initialPlan);
  }, [initialPlan]);

  useEffect(() => {
    const t = timers.current;
    return () => Object.values(t).forEach(clearTimeout);
  }, []);

  const required = (plan.checklist || []).filter((i) => !i.isStretch);
  if (!required.length) return null;

  const label = PLAN_TYPES[planTypeOf(plan)].label;
  const order = planSubjectOrder(plan);
  const focusSubject = subjectFromSessionType(sessionSubject, order);
  const subjectsPresent = order.filter((s) => required.some((i) => i.subject === s));
  const orderedSubjects = focusSubject
    ? [focusSubject, ...subjectsPresent.filter((s) => s !== focusSubject)]
    : subjectsPresent;

  const sessionHoursFor = (it) => {
    const h = (it.progressHistory || []).find((e) => e.sessionLogId && e.sessionLogId === sessionLogId);
    return h ? Number(h.hoursWorked) || 0 : 0;
  };
  const getEdit = (it) => edits[it.id] || { sessionHours: sessionHoursFor(it), pct: itemProgressPct(it) };

  function scheduleSave(it, next) {
    setEdits((p) => ({ ...p, [it.id]: next }));
    setSaving(true);
    pending.current += 1;
    clearTimeout(timers.current[it.id]);
    timers.current[it.id] = setTimeout(() => doSave(it, next), 600);
  }
  async function doSave(it, vals) {
    try {
      const checklist = await upsertItemSessionProgress({
        planId: plan.id,
        itemId: it.id,
        sessionLogId: sessionLogId || null,
        sessionHours: Math.max(0, Number(vals.sessionHours) || 0),
        progressPct: Math.max(0, Math.min(100, Number(vals.pct) || 0)),
        actorUid: tutorUid || null,
        actorName: tutorName || null,
      });
      setPlan((prev) => ({ ...prev, checklist }));
    } catch {
      /* keep UI */
    } finally {
      pending.current = Math.max(0, pending.current - 1);
      if (pending.current === 0) setSaving(false);
    }
  }

  const toggle = (id) =>
    setExpanded((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  return (
    <div className="spp-card">
      <style>{CSS}</style>
      <header className="spp-header">
        <div className="spp-title-group">
          <div className="spp-icon">✦</div>
          <h3 className="spp-title">{plan.title || `${label} Plan`} Progress</h3>
        </div>
        <span className={`spp-saved ${saving ? "spp-saved--busy" : "spp-saved--idle"}`}>
          <span className="spp-dot" />
          {saving ? "Saving…" : "Saved"}
        </span>
      </header>

      <div className="spp-body">
        <p className="spp-hint">Drag the bar to update completion. Use + / − to log hours worked today.</p>

        {orderedSubjects.map((subject) => {
          const items = required.filter((i) => i.subject === subject);
          if (!items.length) return null;
          return (
            <div key={subject}>
              <div className="spp-section-title">
                {subject}
                {focusSubject === subject && <span className="spp-focus">This session</span>}
              </div>
              <div className="spp-list">
                {items.map((it, idx) => {
                  const e = getEdit(it);
                  const pct = Math.max(0, Math.min(100, Number(e.pct) || 0));
                  const status = statusOf(pct);
                  const accent = ACCENT[status];
                  const covered = Number(it.coveredHours) || 0;
                  const sessHrs = Number(e.sessionHours) || 0;
                  const isOpen = expanded.has(it.id);
                  return (
                    <React.Fragment key={it.id}>
                      <div className={`spp-skill ${status === "done" ? "is-done" : pct > 0 ? "is-active" : ""}`}>
                        <div className="spp-main">
                          <div className="spp-topline">
                            <span className="spp-number">{String(it.subjectRank).padStart(2, "0")}</span>
                            <span className={`spp-name ${status === "done" ? "is-done" : ""}`}>{it.subcategory}</span>
                          </div>
                          <div className="spp-meta">
                            {it.majorCategory} · {covered} / {it.allocatedHours}h
                          </div>
                        </div>

                        <div className="spp-progress">
                          <input
                            type="range"
                            min="0"
                            max="100"
                            step="5"
                            value={pct}
                            className="spp-range"
                            style={{ "--value": `${pct}%`, "--spp-accent": accent }}
                            onChange={(ev) => scheduleSave(it, { ...e, pct: Number(ev.target.value) })}
                            aria-label={`${it.subcategory} completion`}
                          />
                          <div className="spp-percent" style={{ color: status === "todo" ? "#475569" : accent }}>
                            {pct}%
                          </div>
                        </div>

                        <div className="spp-hours" aria-label={`Hours worked on ${it.subcategory}`}>
                          <button type="button" onClick={() => scheduleSave(it, { ...e, sessionHours: Math.max(0, sessHrs - 0.5) })}>
                            −
                          </button>
                          <div className="spp-hours-val">{sessHrs}h</div>
                          <button type="button" onClick={() => scheduleSave(it, { ...e, sessionHours: sessHrs + 0.5 })}>
                            +
                          </button>
                        </div>

                        <button className="spp-chev" type="button" onClick={() => toggle(it.id)} aria-label="Details">
                          {isOpen ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                        </button>
                      </div>
                      {isOpen && (
                        <div className="spp-details">
                          <PlanProgressHistory item={it} />
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const CSS = `
.spp-card { background:#fff; border:1px solid #e8ebf0; border-radius:18px; box-shadow:0 10px 30px rgba(15,23,42,0.05); overflow:hidden; }
.spp-header { display:flex; align-items:center; justify-content:space-between; gap:16px; padding:18px 22px; border-bottom:1px solid #f1f5f9; }
.spp-title-group { display:flex; align-items:center; gap:12px; min-width:0; }
.spp-icon { width:32px;height:32px;border-radius:10px;display:grid;place-items:center;background:#f8fafc;border:1px solid #e5e7eb;color:#334155;font-size:15px;flex:0 0 auto; }
.spp-title { margin:0; font-size:16px; font-weight:800; letter-spacing:-0.01em; color:#0f172a; }
.spp-saved { display:inline-flex; align-items:center; gap:8px; border-radius:999px; padding:6px 12px; font-size:12.5px; font-weight:750; white-space:nowrap; }
.spp-saved--idle { color:#059669; background:#ecfdf5; border:1px solid #d1fae5; }
.spp-saved--busy { color:#92400e; background:#fffbeb; border:1px solid #fde68a; }
.spp-dot { width:7px;height:7px;border-radius:50%; background:currentColor; }
.spp-body { padding:16px 22px 22px; }
.spp-hint { color:#6b7280; font-size:13px; margin:0 0 14px; }
.spp-section-title { font-size:11.5px; letter-spacing:0.1em; text-transform:uppercase; color:#475569; font-weight:800; margin:18px 0 10px; display:flex; align-items:center; gap:8px; }
.spp-section-title:first-of-type { margin-top:0; }
.spp-focus { font-size:10px; font-weight:700; background:#dbeafe; color:#1e40af; padding:1px 7px; border-radius:999px; letter-spacing:0; text-transform:none; }
.spp-list { border:1px solid #f1f5f9; border-radius:16px; overflow:hidden; }
.spp-skill { display:grid; grid-template-columns: minmax(180px,1fr) minmax(240px,1.2fr) 110px 34px; gap:20px; align-items:center; padding:15px 18px; border-bottom:1px solid #f1f5f9; transition:background 160ms ease; }
.spp-skill:last-child { border-bottom:0; }
.spp-skill:hover { background:#fbfdff; }
.spp-skill.is-active { background:linear-gradient(90deg,#fffbeb,#fff 70%); }
.spp-skill.is-done { background:linear-gradient(90deg,#ecfdf5,#fff 70%); }
.spp-main { min-width:0; }
.spp-topline { display:flex; align-items:center; gap:10px; min-width:0; }
.spp-number { color:#94a3b8; font-size:12px; font-weight:800; flex:0 0 auto; }
.spp-name { font-size:14.5px; font-weight:750; letter-spacing:-0.01em; color:#0f172a; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.spp-name.is-done { color:#94a3b8; text-decoration:line-through; }
.spp-meta { margin-top:4px; color:#6b7280; font-size:12.5px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.spp-progress { display:grid; grid-template-columns:1fr 44px; align-items:center; gap:12px; min-width:0; }
.spp-percent { text-align:right; font-size:13px; font-weight:850; }
.spp-range { -webkit-appearance:none; appearance:none; width:100%; height:24px; background:transparent; cursor:pointer; outline:none; }
.spp-range::-webkit-slider-runnable-track { height:10px; border-radius:999px; background:linear-gradient(to right, var(--spp-accent) 0%, var(--spp-accent) var(--value), #e2e8f0 var(--value), #e2e8f0 100%); }
.spp-range::-webkit-slider-thumb { -webkit-appearance:none; appearance:none; width:22px;height:22px;border-radius:50%;background:#fff;border:5px solid var(--spp-accent); box-shadow:0 3px 10px rgba(15,23,42,0.16); margin-top:-6px; }
.spp-range::-moz-range-track { height:10px;border-radius:999px;background:#e2e8f0; }
.spp-range::-moz-range-progress { height:10px;border-radius:999px;background:var(--spp-accent); }
.spp-range::-moz-range-thumb { width:14px;height:14px;border-radius:50%;background:#fff;border:5px solid var(--spp-accent); box-shadow:0 3px 10px rgba(15,23,42,0.16); }
.spp-hours { justify-self:end; display:inline-flex; align-items:center; height:34px; border:1px solid #e5e7eb; border-radius:999px; background:#fff; overflow:hidden; }
.spp-hours button { border:0; background:transparent; color:#64748b; width:32px;height:32px;font-size:17px;line-height:1;cursor:pointer; }
.spp-hours button:hover { background:#f8fafc; }
.spp-hours-val { width:46px;height:32px;display:grid;place-items:center;border-left:1px solid #f1f5f9;border-right:1px solid #f1f5f9;color:#111827;font-size:12.5px;font-weight:850; }
.spp-chev { border:0; background:transparent; color:#94a3b8; cursor:pointer; justify-self:center; display:inline-flex; padding:4px; }
.spp-details { padding:4px 18px 16px; border-bottom:1px solid #f1f5f9; background:#fbfdff; }
.spp-list > div:last-child .spp-details { border-bottom:0; }
@media (max-width:850px) {
  .spp-skill { grid-template-columns:1fr 34px; row-gap:12px; }
  .spp-progress { grid-column:1 / 2; }
  .spp-hours { justify-self:start; }
}
`;
